import { useState, useRef, useEffect, useMemo } from 'react'
import { Badge } from 'components/Badge/Badge'
import { NavBar } from 'components/NavBar/NavBar'
import { ColoredDot, Flex, H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { DisplayCard } from 'components/DisplayCards/DisplayCard'
import {
    ActionsMenuWrapper,
    ActionsMenuButton,
    ActionsDropdown,
    ActionsDropdownItem,
    PhaseList,
    PhaseItem,
    PhaseIndicator,
    PhaseDot,
} from './SinglePlan.styled'
import { Container, ContainerHeader, ContainerContent } from 'components/Containers/Containers'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import {
    cancelOperationPlan,
    completeOperationPlan,
    getSingleOperationPlan,
    pauseOperationPlan,
    resumeOperationPlan,
} from 'features/operations/plansSlice'
import { phaseStatuses, planStatuses, STATUS_COLORS } from '../plansData'

type IPlanMenuAction = {
    label: string
    danger?: boolean
    onClick: () => void
}

export const SinglePlan = () => {
    const [showActions, setShowActions] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const { id } = useParams()
    const navigate = useNavigate()
    const { singlePlan } = useSelector((state: RootState) => state.operationPlans)
    const dispatch = useDispatch<AppDispatch>()

    useEffect(() => {
        dispatch(getSingleOperationPlan(id))
    }, [dispatch, id])

    const actionItems = useMemo((): IPlanMenuAction[] => {
        const runAction = (action: typeof cancelOperationPlan) => () => {
            setShowActions(false)
            dispatch(action({ id: Number(id), single: true }))
        }

        const items: IPlanMenuAction[] = [
            {
                label: 'Duplicate',
                onClick: () => {
                    setShowActions(false)
                    navigate('duplicate')
                },
            },
        ]

        const cancelItem: IPlanMenuAction = {
            label: 'Cancel plan',
            danger: true,
            onClick: runAction(cancelOperationPlan),
        }

        if (singlePlan?.status === 1) {
            items.push(cancelItem)
        } else if (singlePlan?.status === 2) {
            items.push(
                { label: 'Pause', onClick: runAction(pauseOperationPlan) },
                { label: 'Complete', onClick: runAction(completeOperationPlan) },
                cancelItem,
            )
        } else if (singlePlan?.status === 3) {
            items.push(
                { label: 'Resume', onClick: runAction(resumeOperationPlan) },
                cancelItem,
            )
        }

        return items
    }, [singlePlan?.status, dispatch, id, navigate])

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setShowActions(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    return <>
        <NavBar />
        <PageWrapper>
            <Flex center gap='1rem'>
                <H1WithBack title={singlePlan?.name} arrow />
                <Badge hexColor={planStatuses.find((status) => status.value === singlePlan?.status)?.color}>
                    <Flex gap='1rem' center>
                        <ColoredDot color={planStatuses.find((status) => status.value === singlePlan?.status)?.color} />
                        <BodyText>{planStatuses.find((status) => status.value === singlePlan?.status)?.label}</BodyText>
                    </Flex>
                </Badge>
                <BodyText grey>{singlePlan?.code}</BodyText>
                <ActionsMenuWrapper ref={menuRef}>
                    <ActionsMenuButton onClick={() => setShowActions((p) => !p)}>
                        <span />
                        <span />
                        <span />
                    </ActionsMenuButton>
                    <ActionsDropdown show={showActions}>
                        {actionItems.map((item) => (
                            <ActionsDropdownItem key={item.label} $danger={item.danger} onClick={item.onClick}>
                                <OtherText>{item.label}</OtherText>
                            </ActionsDropdownItem>
                        ))}
                    </ActionsDropdown>
                </ActionsMenuWrapper>
            </Flex>
            <Flex gap='1rem' style={{ margin: '2rem 0' }}>
                <DisplayCard value='N/A' label='Tasks generated' borderColor='#ff9900' />
                <DisplayCard value='N/A' label='Completed' borderColor='#ff9900' />
                <DisplayCard value='N/A' label='Queue depth' borderColor='#ff9900' />
                <DisplayCard value='N/A' label='Drift' borderColor='#ff9900' />
                <DisplayCard value='N/A' label='Workers active' borderColor='#ff9900' />
                <DisplayCard value='N/A' label='Trigger fires' borderColor='#ff9900' />
            </Flex>
            <Flex gap='1rem' width='100%' >
                <Flex column gap='1rem' width='70%'>
                    <Container>
                        <ContainerHeader>
                            <Flex between center width='100%'>
                                <TitleText weight='bold'>Phases</TitleText>
                            </Flex>
                        </ContainerHeader>
                        <ContainerContent>
                            <PhaseList>
                                {singlePlan?.phases?.map((phase) => (
                                    <PhaseItem key={phase.id}>
                                        <PhaseIndicator>
                                            <PhaseDot />
                                        </PhaseIndicator>
                                        <Flex column gap='0.25rem'>
                                            <Flex center gap='0.5rem'>
                                                <OtherText grey>Phase {phase.id}</OtherText>
                                                <BodyText weight='bold'>{phase.name}</BodyText>
                                                <Badge hexColor={phaseStatuses.find((status) => status.value === phase.status)?.color}>
                                                    <Flex gap='1rem' center >
                                                        <ColoredDot color={phaseStatuses.find((status) => status.value === phase.status)?.color} />
                                                        <BodyText>{phaseStatuses.find((status) => status.value === phase.status)?.label}</BodyText>
                                                    </Flex>
                                                </Badge>
                                            </Flex>
                                        </Flex>
                                    </PhaseItem>
                                ))}
                            </PhaseList>
                        </ContainerContent>
                    </Container>

                    <Container>
                        <ContainerHeader>
                            <TitleText weight='bold'>Rolling Horizon</TitleText>
                        </ContainerHeader>
                        <ContainerContent>
                            <Flex width='100%' >
                                <Flex column gap='0.2rem' width='100%' >
                                    <OtherText grey>LOOKAHEAD</OtherText>
                                    <TitleText>Next 6 Hours</TitleText>
                                </Flex>
                                <Flex column gap='0.2rem' width='100%' >
                                    <OtherText grey>SCHEDULER</OtherText>
                                    <TitleText>Next 6 Hours</TitleText>
                                </Flex>
                                <Flex column gap='0.2rem' width='100%' >
                                    <OtherText grey>NEXT RUN</OtherText>
                                    <TitleText>Next 6 Hours</TitleText>
                                </Flex>
                            </Flex>
                        </ContainerContent>
                    </Container>
                </Flex>
                <Flex width='30%' column gap='1rem' >
                    <Container>
                        <ContainerHeader>
                            <TitleText weight='bold'>Alerts · last 24h</TitleText>
                        </ContainerHeader>
                        <ContainerContent>
                            <BodyText grey>No alerts in the last 24 hours.</BodyText>
                        </ContainerContent>
                    </Container>

                    <Container>
                        <ContainerHeader>
                            <TitleText weight='bold'>Queue health by department
                            </TitleText>
                        </ContainerHeader>
                        <ContainerContent>

                        </ContainerContent>
                    </Container>
                </Flex>

            </Flex>
        </PageWrapper>
    </>
}